import { ArrowUp2, ArrowDown2 } from 'iconsax-reactjs';
import { GAP_BIAS_TIMEFRAMES, TIMEFRAMES, type Timeframe, timeframeShort } from '@pipyar/core';
import { cn } from '@/lib/utils';
import type { GapSummaryRow } from './api';
import { useI18n } from '@/lib/i18n';

interface GapSummaryGridProps {
  rows: GapSummaryRow[] | undefined;
  digits: number;
  isLoading?: boolean;
  /** Highlighted frame, when shown next to a chart. */
  active?: Timeframe;
  onSelect?: (tf: Timeframe) => void;
  /** 'grid' tiles every frame; 'compact' is the denser sidebar form. */
  variant?: 'grid' | 'compact';
  className?: string;
}

/**
 * Unfilled gap counts per timeframe.
 *
 * Shared by the Gaps sidebar and the dashboard widget rather than written twice,
 * so the two can never drift into disagreeing about the same numbers.
 *
 * "Above" and "below" are stated from the price's point of view, which is the
 * opposite of the gap's direction: an UP gap leaves an unfilled level BELOW the
 * current price, because price jumped away from it upward and has to come back
 * down to fill it. Labelling those tiles "up" would put the arrow on the wrong
 * side of every trade.
 */
export function GapSummaryGrid({
  rows,
  digits,
  isLoading = false,
  active,
  onSelect,
  variant = 'grid',
  className,
}: GapSummaryGridProps) {
  const byTimeframe = new Map((rows ?? []).map((r) => [r.timeframe, r]));

  const { t } = useI18n();

  return (
    <div
      className={cn(
        'grid gap-2',
        variant === 'grid' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-2',
        className,
      )}
    >
      {TIMEFRAMES.map((tf) => {
        const row = byTimeframe.get(tf);
        const isActive = active === tf;
        const interactive = Boolean(onSelect);
        // Shown but not voting on the dashboard bias.
        const countsTowardBias = GAP_BIAS_TIMEFRAMES.includes(tf);

        return (
          <button
            key={tf}
            type="button"
            disabled={!interactive}
            onClick={() => onSelect?.(tf)}
            aria-current={isActive ? 'true' : undefined}
            className={cn(
              'rounded-xl border p-2.5 text-start transition-colors',
              interactive && 'hover:border-primary/40 hover:bg-muted/60 cursor-pointer',
              !interactive && 'cursor-default',
              isActive
                ? 'border-primary/50 bg-primary/8'
                : 'border-border bg-surface/50',
            )}
          >
            <div className="flex items-baseline justify-between">
              <span
                className={cn(
                  'text-xs font-semibold',
                  isActive ? 'text-primary' : 'text-foreground',
                )}
              >
                {timeframeShort(tf)}
              </span>
              {isLoading && !row ? (
                <span className="h-3 w-6 animate-pulse rounded bg-muted" />
              ) : (
                <span className="flex items-center gap-1">
                  {!countsTowardBias && (
                    <span
                      title={t('gaps.derivedNote')}
                      className="text-[9px] leading-none text-muted-foreground/60"
                    >
                      ○
                    </span>
                  )}
                  <span className="tabular text-[10px] text-muted-foreground">
                    {row?.total ?? 0}
                  </span>
                </span>
              )}
            </div>

            <div className="mt-2 flex items-center gap-3">
              {/* Gap left BELOW price (created by an up move). */}
              <span className="flex items-center gap-1" title={t('gaps.unfilledBelow')}>
                <ArrowDown2 size={13} variant="Bold" className="text-bear" />
                <span className="tabular text-sm font-medium">{row?.up ?? 0}</span>
              </span>
              {/* Gap left ABOVE price (created by a down move). */}
              <span className="flex items-center gap-1" title={t('gaps.unfilledAbove')}>
                <ArrowUp2 size={13} variant="Bold" className="text-bull" />
                <span className="tabular text-sm font-medium">{row?.down ?? 0}</span>
              </span>
            </div>

            {variant === 'grid' && (
              <div className="mt-1.5 space-y-0.5 text-[10px] leading-tight text-muted-foreground">
                <div className="tabular">
                  {row?.nearestUp != null ? row.nearestUp.toFixed(digits) : '—'}{' '}
                  <span className="opacity-60">below</span>
                </div>
                <div className="tabular">
                  {row?.nearestDown != null ? row.nearestDown.toFixed(digits) : '—'}{' '}
                  <span className="opacity-60">above</span>
                </div>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Totals that feed the bias, counting only GAP_BIAS_TIMEFRAMES.
 *
 * H4/D1/W1 are excluded: derived from 17 years of H1, their gaps are mostly
 * decade-old levels far below current price, which held the dashboard at bearish
 * no matter what was happening intraday. See GAP_BIAS_TIMEFRAMES.
 */
export function totalGaps(rows: GapSummaryRow[] | undefined): {
  below: number;
  above: number;
  total: number;
  counted: readonly Timeframe[];
} {
  const counted = (rows ?? []).filter((r) => GAP_BIAS_TIMEFRAMES.includes(r.timeframe));
  const below = counted.reduce((n, r) => n + r.up, 0);
  const above = counted.reduce((n, r) => n + r.down, 0);
  return { below, above, total: below + above, counted: GAP_BIAS_TIMEFRAMES };
}
